import React from 'react'

/**
 * @typedef {{
 * routingNumber: string,
 * accountNumber: string,
 * accountNumberConfirmation: string,
 * accountType: string,
 * accountName: string,
 * country: string,
 * }} BankAccountInformation
 */

/**
 * @typedef {{
 * bankAccountInformation: BankAccountInformation,
 * updateBankAccountField: (field: keyof BankAccountInformation, value: string) => void,
 * resetBankAccount: () => void,
 * isValidBankAccount: boolean,
 * }} PaymentBankAccountContextType
 */

const initialBankAccountState = {
  routingNumber: '',
  accountNumber: '',
  accountNumberConfirmation: '',
  accountType: '',
  accountName: '',
  country: '',
}

const PaymentBankAccountContext = React.createContext(
  /** @type {PaymentBankAccountContextType} */ ({}),
)

export function PaymentBankAccountProvider({children}) {
  const [bankAccountInformation, setBankAccountInformation] = React.useState(
    initialBankAccountState,
  )

  const updateBankAccountField = React.useCallback((field, value) => {
    setBankAccountInformation(prev => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const resetBankAccount = React.useCallback(() => {
    setBankAccountInformation(initialBankAccountState)
  }, [])

  const isValidBankAccount = React.useMemo(() => {
    const {
      routingNumber,
      accountNumber,
      accountNumberConfirmation,
      accountType,
      accountName,
      country,
    } = bankAccountInformation

    return (
      routingNumber.length > 0 &&
      accountNumber.length > 0 &&
      accountNumberConfirmation.length > 0 &&
      accountNumber === accountNumberConfirmation &&
      accountType.length > 0 &&
      accountName.length > 0 &&
      country.length > 0
    )
  }, [bankAccountInformation])

  const contextValue = React.useMemo(
    () => ({
      bankAccountInformation,
      updateBankAccountField,
      resetBankAccount,
      isValidBankAccount,
    }),
    [
      bankAccountInformation,
      updateBankAccountField,
      resetBankAccount,
      isValidBankAccount,
    ],
  )

  return (
    <PaymentBankAccountContext.Provider value={contextValue}>
      {children}
    </PaymentBankAccountContext.Provider>
  )
}

export function usePaymentBankAccount() {
  const context = React.useContext(PaymentBankAccountContext)
  if (!context) {
    throw new Error(
      'usePaymentBankAccount must be used within a PaymentBankAccountProvider',
    )
  }
  return context
}
